import { Component, OnInit } from "@angular/core";
import { Chart, registerables } from "chart.js";
import { RETURNS_DATASETS_LABELS, STYLES } from "../../constants/analytics.constant";
import {
  DateState,
  RefoundsAnalyticsDayDto,
  RefundsAnalyticsDto,
  ReturnAnalyticsDay,
  ReturnBarState,
  ReturnsPercents,
} from "../../models/analytics.model";
import { AnalyticsService } from "../../services/analytics-data.service";
import { takeUntil } from "rxjs";
import { UnsubscribeService } from "src/app/services/unsubscribe.service";
import { TranslateService } from "@ngx-translate/core";
import { AnalyticsTranslationService } from "../../services/analytics-translation.service";

Chart.register(...registerables);

@Component({
  selector: "app-returns-analytics",
  templateUrl: "./returns-analytics.component.html",
  styleUrls: ["./returns-analytics.component.scss"],
  providers: [UnsubscribeService],
})
export class ReturnsAnalyticsComponent implements OnInit {
  chart: any;
  data: any;
  commonData: RefundsAnalyticsDto | null = null;

  dateState = DateState;
  dateFilter = DateState.Month;
  returnBarState = ReturnBarState;
  barStates = new Set<ReturnBarState>();
  barState = ReturnBarState;
  totalNumberRefounds: number | null = null;
  totalSumRefounds: number | null = null;
  returnStatistics: ReturnAnalyticsDay[] | null = null;

  percents: ReturnsPercents | null = null;

  isDataValid = true;

  constructor(
    private analyticsService: AnalyticsService,
    private translateService: TranslateService,
    private unsubscribe$: UnsubscribeService,
    private analyticsTranslationService: AnalyticsTranslationService
  ) {}

  ngOnInit() {
    this.loadInfo(true);

    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.updateLabelTranslations();
      this.setLabels();
      this.chart?.update();
    });
  }

  loadInfo(isFirstCall?: boolean) {
    const parameters = { period_type: this.dateFilter };

    this.analyticsService
      .getRefundsAnalytics(parameters)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.commonData = res;
          this.totalNumberRefounds = res.qty_total;
          this.totalSumRefounds = res.sum_total;

          this.isDataValid = res.qty_total <= 0 ? false : true;

          this.percents = {
            p2pSum: Math.round(((res.total_statistics.find((s: any) => s.payment_type === "P2P_REFUNDS")?.refund_sum || 0) / res.sum_total) * 100),
            transitionSum: Math.round(((res.total_statistics.find((s: any) => s.payment_type === "RETURNS")?.refund_sum || 0) / res.sum_total) * 100),
            externalSum: Math.round(
              ((res.total_statistics.find((s: any) => s.payment_type === "CASH_REFUNDS")?.refund_sum || 0) / res.sum_total) * 100
            ),
          };

          this.returnStatistics = res.statistics.map((stat) => {
            const statistic = this.dateFilter === DateState.Year ? stat.month_statistics : stat.statistics;

            return {
              date: stat.date,
              refundSum: stat.refund_sum,
              quantity: stat.quantity,
              returnsByP2pSum: statistic.find((s: any) => s.refund_type === "P2P_REFUNDS")?.sum || 0,
              returnsByCash: statistic.find((s: any) => s.refund_type === "CASH_REFUNDS")?.sum || 0,
              returnsByTransitAccount: statistic.find((s: any) => s.refund_type === "RETURNS")?.sum || 0,
            };
          });

          if (isFirstCall) {
            this.renderChar();
          } else {
            this.data.datasets = this.initializeChartData().datasets;
          }

          this.initAllBars();
          this.setLabels();
          this.chart.update();
        },
      });
  }

  setTimeFrame(dateState: DateState) {
    if (dateState === this.dateFilter) return;
    this.dateFilter = dateState;
    switch (dateState) {
      case DateState.Week: {
        this.dateFilter = dateState;
        break;
      }
      case DateState.Month: {
        this.dateFilter = dateState;
        break;
      }
      case DateState.Year:
        this.dateFilter = dateState;
        break;
    }

    this.loadInfo();
  }

  setData(state: ReturnBarState) {
    state === ReturnBarState.AllPayments ? this.toggleAllBars() : this.toggleBar(state);

    this.chart.update();
  }

  private initAllBars() {
    this.barStates.add(ReturnBarState.AllPayments);
    this.barStates.add(ReturnBarState.ExternalSystems);
    this.barStates.add(ReturnBarState.P2p);
    this.barStates.add(ReturnBarState.TransitAccount);
  }

  private setLabels() {
    if (!this.returnStatistics) return;
    switch (this.dateFilter) {
      case DateState.Month:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.returnStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
      case DateState.Week:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.returnStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
      case DateState.Year:
        this.data.labels = this.analyticsTranslationService.mapDatesToLabels(
          this.returnStatistics.map((el) => el.date),
          this.dateFilter
        );
        break;
    }
  }

  private mapItemStateToDto(itemState: ReturnBarState) {
    switch (itemState) {
      case 0:
        return "P2P_REFUNDS";
      case 1:
        return "RETURNS";
      case 2:
        return "CASH_REFUNDS";
      default:
        return;
    }
  }

  private updateLabelTranslations() {
    if (!this.data) return;

    this.data.datasets.forEach((dataset: any, index: number) => {
      dataset.label = this.translateService.instant(`ANALYTICS_PAGE.${RETURNS_DATASETS_LABELS[index]}`);
    });
  }

  private toggleBar(itemState: ReturnBarState) {
    if (this.barStates.has(itemState)) {
      this.barStates.delete(itemState);
      this.totalNumberRefounds =
        this.totalNumberRefounds! -
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.quantity || 0);
      this.totalSumRefounds =
        this.totalSumRefounds! -
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.refund_sum || 0);
    } else {
      this.barStates.add(itemState);
      this.totalNumberRefounds =
        this.totalNumberRefounds! +
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.quantity || 0);
      this.totalSumRefounds =
        this.totalSumRefounds! +
        (this.commonData!.total_statistics.find((s: any) => s.payment_type === this.mapItemStateToDto(itemState))?.refund_sum || 0);
    }

    this.data.datasets
      .filter((el: any) => el.bar === itemState)
      .forEach((item: any) => {
        item.hidden = !this.barStates.has(itemState);
        if (
          this.barStates.has(ReturnBarState.ExternalSystems) &&
          this.barStates.has(ReturnBarState.P2p) &&
          this.barStates.has(ReturnBarState.TransitAccount)
        ) {
          this.barStates.add(ReturnBarState.AllPayments);
        } else {
          this.barStates.delete(ReturnBarState.AllPayments);
        }
      });
  }
  private toggleAllBars() {
    if (this.barStates.has(ReturnBarState.AllPayments)) {
      this.totalNumberRefounds = 0;
      this.totalSumRefounds = 0;
      Object.values(ReturnBarState)
        .filter((el: any) => !isNaN(el))
        .map((el) => {
          this.barStates.delete(el as ReturnBarState);
        });
    } else {
      this.totalNumberRefounds =
        this.commonData?.total_statistics.reduce((accumulator, current) => {
          return accumulator + current.quantity;
        }, 0) || 0;
      this.totalSumRefounds =
        this.commonData?.total_statistics.reduce((accumulator, current) => {
          return accumulator + current.refund_sum;
        }, 0) || 0;
      Object.values(ReturnBarState)
        .filter((el: any) => !isNaN(el))
        .map((el) => {
          this.barStates.add(el as ReturnBarState);
        });
    }

    this.data.datasets.forEach((item: any) => {
      item.hidden = !this.barStates.has(item.bar);
    });
  }

  private calculateTotalSum(date: string) {
    const index = this.data.labels.indexOf(date);
    if (index !== -1) {
      const stat = this.returnStatistics![index];
      let totalSum = 0;

      if (this.barStates.has(this.barState.P2p)) {
        totalSum += stat.returnsByP2pSum;
      }
      if (this.barStates.has(this.barState.TransitAccount)) {
        totalSum += stat.returnsByTransitAccount;
      }
      if (this.barStates.has(this.barState.ExternalSystems)) {
        totalSum += stat.returnsByCash;
      }

      return Number.isInteger(totalSum) ? totalSum : totalSum.toFixed(2).replace(".", ",");
    }

    return 0;
  }

  private getMinBarHeightPlugin() {
    const dateFilter = this.dateFilter;
    const barStates = this.barStates;

    return {
      id: "minBarHeightPlugin",
      beforeDatasetsDraw: function (chart: any) {
        const ctx = chart.ctx;
        const datasets = chart.data.datasets;
        const minBarHeight = 2;

        if (dateFilter === DateState.Month) {
          datasets.forEach((dataset: any, datasetIndex: any) => {
            if (!barStates.has(dataset.bar)) {
              return;
            }
            const meta = chart.getDatasetMeta(datasetIndex);
            if (!meta.hidden) {
              meta.data.forEach((bar: any, index: any) => {
                const dataValue = dataset.data[index];
                if (dataValue > 50) {
                  return;
                }
                const originalHeight = bar.height;
                const newHeight = Math.max(originalHeight, minBarHeight);
                const heightIncrease = newHeight - originalHeight;
                const y = bar.y - heightIncrease / 2;
                const x = bar.x - bar.width / 2;

                if (dataValue === 0) {
                  return;
                }

                ctx.save();
                ctx.fillStyle = dataset.backgroundColor;
                ctx.fillRect(x, y, bar.width, newHeight);
                ctx.restore();
              });
            }
          });
        }
      },
    };
  }

  renderChar() {
    this.data = this.initializeChartData();

    const minBarHeightPlugin = this.getMinBarHeightPlugin();

    const hoverSegment = {
      hoverValue: null as null | number,
      id: "hoverSegment",
      beforeDatasetsDraw(chart: any) {
        const {
          ctx,
          chartArea: { top, width, height },
          scales: { x },
        } = chart;
        ctx.save();

        const segment = width / (x.max + 1);

        ctx.fillStyle = "rgba(173, 216, 230, 0.2)";

        if (this.hoverValue || this.hoverValue === 0) {
          ctx.fillRect(x.getPixelForValue(this.hoverValue) - segment / 2, top, segment, height);
        }
      },
      afterEvent(chart: any, args: any) {
        const {
          scales: { x },
        } = chart;

        if (args.inChartArea) {
          this.hoverValue = x.getValueForPixel(args.event.x);
        } else {
          this.hoverValue = null;
        }

        args.changed = true;
      },
    };

    this.chart = new Chart("return-bar", {
      type: "bar",
      data: this.data,
      plugins: [hoverSegment, minBarHeightPlugin],
      options: {
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
            position: "bottom",
          },
          tooltip: {
            enabled: false,
            external: (context) => {
              let tooltipEl = document.getElementById("chartjs-tooltip");
              const tooltipModel = context.tooltip;

              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
                tooltipEl.innerHTML = "<table></table>";
                document.body.appendChild(tooltipEl);
              }

              if (tooltipModel.opacity === 0 || allDataZero()) {
                tooltipEl.style.opacity = "0";
                return;
              }

              function allDataZero() {
                const bodyLines = tooltipModel.body.map(getBody);
                for (let i = 0; i < bodyLines.length; i++) {
                  if (parseInt(bodyLines[i][0].split(":")[1]) !== 0) {
                    return false;
                  }
                }
                return true;
              }

              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
              } else {
                tooltipEl.classList.add("no-transform");
              }

              function getBody(bodyItem: any) {
                return bodyItem.lines;
              }

              if (tooltipModel.body) {
                const titleLines = tooltipModel.title || [];
                const bodyLines = tooltipModel.body.map(getBody);
                let innerHtml = "";
                titleLines.forEach((title, i) => {
                  innerHtml += `
                  <thead style="${STYLES.marginBottom(16)}">
                    <tr>
                      <th style="${STYLES.font.common} ${STYLES.font.bold} ${STYLES.title.textLeft}">
                        <div style="${STYLES.div.flexBetween}">
                          <span>${title}</span>
                          <span>₸${this.calculateTotalSum(title)}</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <div style="${STYLES.height(6)}"></div>
                  <div style="${STYLES.div.flexBetween}">
                  <span style="${STYLES.font.common} ${STYLES.font.normal}"> ${this.translateService.instant(`ANALYTICS_PAGE.RETURNS_SUM`)} </span>
                  <span style="${STYLES.font.common} ${STYLES.font.bold}"> ${this.returnStatistics![this.data.labels.indexOf(title)].quantity}</span>
                  </div>
                  `;
                });

                bodyLines.forEach((body, i) => {
                  const description = body[0].split(":")[0];
                  const value = "₸" + body[0].split(":")[1];
                  const colors = tooltipModel.labelColors[i];

                  innerHtml += `
                  <tbody>
                    <tr>
                      <td>
                        <div style="${STYLES.div.flexBetween}">
                          <div style="${STYLES.div.flex} ${STYLES.gap(4)} ${STYLES.div.alignCenter}">
                            <div style="${STYLES.width(8)} ${STYLES.height(8)} ${STYLES.borderRadius(100)} background-color: ${
                              colors.backgroundColor
                            }; border: 1px solid ${colors.borderColor};"></div>
                            <span style="${STYLES.font.common} ${STYLES.font.normal}"> ${description}</span>
                          </div>
                          <span style="${STYLES.font.common} ${STYLES.font.bold}"> ${value}</span>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  `;
                });

                (tooltipEl.querySelector("table") as HTMLTableElement).innerHTML = innerHtml;
              }

              const position = context.chart.canvas.getBoundingClientRect();

              const tooltipStyle = tooltipEl.style;

              Object.assign(tooltipStyle, {
                backgroundColor: "#FFF",
                width: "241px",
                padding: "8px 12px",
                position: "absolute",
                opacity: "1",
                transition: "0.3s",
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                borderRadius: "6px",
                boxShadow: "0px 4px 16px 0px rgba(112, 112, 112, 0.25)",
                left: position.left / 1.75 + window.scrollX + tooltipModel.caretX + "px",
                top: position.top + window.scrollY + tooltipModel.caretY + "px",
                pointerEvents: "none",
              });
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {},
          y: {
            border: {
              dash: [3, 4],
            },
          },
        },
      },
    });
  }

  private initializeChartData() {
    const data = {
      labels: this.analyticsTranslationService.mapDatesToLabels(
        this.returnStatistics!.map((el) => el.date),
        this.dateFilter
      ),
      datasets: [
        {
          label: "P2P операция",
          data: this.returnStatistics?.map((stat) => stat.returnsByP2pSum),
          backgroundColor: ["rgba(255, 160, 48, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: ReturnBarState.P2p,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "С транзитного счёта",
          data: this.returnStatistics?.map((stat) => stat.returnsByTransitAccount),
          backgroundColor: ["rgba(59, 187, 147, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: ReturnBarState.TransitAccount,
          stack: "group",
          categoryPercentage: 0.55,
        },
        {
          label: "Через внешние системы",
          data: this.returnStatistics?.map((stat) => stat.returnsByCash),
          backgroundColor: ["rgba(225, 71, 191, 1)"],
          borderRadius: 4,
          order: 1,
          hidden: false,
          bar: ReturnBarState.ExternalSystems,
          stack: "group",
          categoryPercentage: 0.55,
        },
      ],
    };

    return data;
  }
}
